import { useMutation } from '@apollo/client';
import {
  useCallback, useContext, useState, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { datadogLogs } from '@datadog/browser-logs';
import {
  Typography, Box, ActionContext, Stack,
} from '../../../..';
import { getSubAccountName } from '../../../../../interfaces';
import { formatMoneyValue } from '../../../../../util';
import { useGlobalToast } from '../../../../../providers/globalToastProvider';
import { useThemeTokens } from '../../../../../providers/themeTokenProvider';
import {
  CREATE_CUSTODIAN_TRANSFER,
  CREATE_DEPOSIT_TRANSFER, CREATE_EXTERNAL_TRANSFER, CREATE_SCHEDULED_DEPOSIT_TRANSFER,
  TRANSFER_ALL_BETWEEN_SUB_ACCOUNTS, TRANSFER_BETWEEN_SUB_ACCOUNTS,
} from './depositReview.queries';
import { translateBackend } from '../../../../../assets/i18n/config';
import { useLocalization } from '../../../../../util/useLocalization';
import { DepositTransferContext } from '../../../depositWorkflow/depositWorkflow';
import { WorkflowContext } from '../../workflowCompletion';
import { updateContextTransfer } from '../../../../../util/updateWorkflowContext';
import { FeatureFlagTypes } from '../../../../../interfaces/organizationFeatureFlag';
import { UserContext } from '../../../../../providers/userContextProvider';
import { WorkflowCompletionForm } from '../../components/workflowCompletionForm';
import { useInactivityTimeout } from '../../../../../providers/inActivityTimeoutProvider';

type HeaderContextArgs = {
  sessionId?: string;
};

type CustodianDepositTransferResponse = {
  status: string;
  confirmationNumber: string;
  action?: {
    type: string;
    details?: { key: string; value: unknown }[]
  } | null;
};

type WorkflowCompletionStatus = 'success' | 'failure' | 'error' | 'deny' | 'silent';

export const DepositReview = ({
  options, onNext, dummyDeposit, setDummyDeposit,
}: {
  options: any, onNext: any, dummyDeposit?: any, setDummyDeposit?: any,
}) => {
  const { t } = useTranslation('workflowCompletions');
  const { localizedDate } = useLocalization();
  const { transferData, setTransferData } = useContext(DepositTransferContext);
  const { activeOrganization } = useContext(UserContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { custodianConnection } = useContext(UserContext);
  const applicableData = dummyDeposit ?? (Object.keys(transferData).length !== 0 ? transferData : workflowData);
  const setApplicationContext = setDummyDeposit ?? (Object.keys(transferData).length !== 0 ? setTransferData : undefined);
  const setWorkflowCompletionContext = Object.keys(transferData).length === 0 ? setWorkflowData : undefined;
  const { refetch, refreshWidgets } = useContext(ActionContext);
  const [loading, setLoading] = useState<boolean>(false);
  const { showToast } = useGlobalToast();
  const usTransferFlowEnabled: boolean = activeOrganization.availableFeatureFlags?.includes(FeatureFlagTypes.US_TRANSFER_FLOW) ?? false;
  const enableMultiCurrencyDepositTransfers: boolean = activeOrganization.availableFeatureFlags?.includes(FeatureFlagTypes.MULTI_CURRENCY_DEPOSIT_TRANSFERS) ?? false;
  const participantNumberEnabled: boolean = usTransferFlowEnabled && applicableData.subAccount?.account?.type?.includes('USA_');
  const { attemptSilentRefresh } = useInactivityTimeout();

  // Add new states for workflow completion control
  const custodianActionTakenRef = useRef(false);
  // Refs for immediate access to prevent race conditions with React's async state updates
  const workflowCompletedRef = useRef(false);
  const hasErrorRef = useRef(false);

  const recordWorkflowTransfers = useCallback(async (transfer: { id: string }) => {
    await setWorkflowData((data) => ({
      ...data,
      currentTransferId: transfer.id,
      createdTransfers: [...data?.createdTransfers ?? [], transfer],
    }));
  }, [setWorkflowData]);
  const recordWorkflowScheduledTransfers = useCallback(async (scheduledTransfer: { id: string }) => {
    await setWorkflowData((data) => ({
      ...data,
      createdScheduledTransfers: [...data?.createdScheduledTransfers ?? [], scheduledTransfer],
    }));
  }, [setWorkflowData]);

  type ManualProcess = NonNullable<typeof workflowData['createdExternalTransfers']>[number];
  const recordWorkflowExternalTransfer = useCallback(async (transfer: ManualProcess) => {
    await setWorkflowData((data) => ({
      ...data,
      createdExternalTransfers: [...data?.createdExternalTransfers ?? [], transfer],
    }));
  }, [setWorkflowData]);
  const recordWorkflowCustodianDepositTransfer = useCallback(async (transfer: NonNullable<typeof workflowData['createdCustodianDepositTransfers']>[number]) => {
    await setWorkflowData((data) => ({
      ...data,
      createdCustodianDepositTransfers: [...data?.createdCustodianDepositTransfers ?? [], transfer],
    }));
  }, [setWorkflowData]);

  const handleWorkflowCompletion = useCallback(async (status: WorkflowCompletionStatus = 'success') => {
    if (workflowCompletedRef.current) return; // Prevent double completion

    workflowCompletedRef.current = true; // Immediate access for race condition prevention
    await onNext();
    await refetch();
    if (refreshWidgets) refreshWidgets();
    setLoading(false);

    // Only show toast for non-silent completions
    switch (status) {
      case 'success':
        showToast({
          message: t('depositReview.success'),
          severity: 'success',
        });
        break;
      case 'failure':
        showToast({
          message: t('depositReview.failure'),
          severity: 'error',
        });
        break;
      case 'deny':
        showToast({
          message: t('depositReview.deny'),
          severity: 'error',
        });
        break;
      case 'error':
        showToast({
          message: t('depositReview.error'),
          severity: 'error',
        });
        break;
      case 'silent':
        // Complete workflow without showing any toast
        // Useful when you want to complete the workflow without showing a toast
        // E.g. when redirecting to external apps like a risk challenge app for ATB
        break;
    }
  }, [onNext, refetch, refreshWidgets, showToast, t]);

  const handleCustodianRiskEngineChallenge = async (
    cmid: string | null,
    data: CustodianDepositTransferResponse,
  ) => {
    const { action } = data;
    const challengedTransfersEnabled = custodianConnection?.enableChallengedCustodianDepositTransfer === true;
    const challengeURL = custodianConnection?.metadata?.find((m) => m.key === 'challengeUrl')?.value as string;
    const transactionId = action?.details?.find((d) => d.key === 'transactionId')?.value as string;
    if (!custodianConnection?.enableChallengedCustodianDepositTransfer) {
      datadogLogs.logger.warn(
        'Custodian Deposit Transfer - enableChallengedCustodianDepositTransfer is not set. Cannot proceed to challenge',
      );
      await handleWorkflowCompletion('error');
      return;
    }

    if (!challengeURL) {
      datadogLogs.logger.warn(
        'Custodian Deposit Transfer - custodian connection does not have challengeUrl. Cannot proceed to challenge',
      );
      await handleWorkflowCompletion('error');
      return;
    }

    if (!transactionId) {
      datadogLogs.logger.warn(
        'Custodian Deposit Transfer - No transactionId was returned. Cannot proceed to challenge',
      );
      await handleWorkflowCompletion('error');
      return;
    }

    if (action && challengedTransfersEnabled && challengeURL && transactionId) {
      if (!cmid) {
        datadogLogs.logger.warn(
          'Custodian Deposit Transfer - CMID is not set. Cannot proceed to challenge',
        );
        await handleWorkflowCompletion('error');
        return;
      }

      // Refresh access token to reset its validity window
      if (attemptSilentRefresh) {
        attemptSilentRefresh();
      }

      const { protocol, host, pathname } = window.location;
      const inboundRedirectUrl = `${protocol}//${host}${pathname}?atbmeta=transactionId:${transactionId}`;
      const toUrlEncode = `session-id=${cmid}&transaction-id=${transactionId}&redirect=${inboundRedirectUrl}`;
      const outboundRedirectUrl = `${challengeURL}?${encodeURIComponent(toUrlEncode)}`;

      try {
        datadogLogs.logger.info(
          'Custodian Deposit Transfer - will redirect to custodian risk challenge',
          { cmid, transactionId },
        );

        // Complete workflow first (this closes the workflow dialog)
        // then redirect in quick succession
        await handleWorkflowCompletion('silent');
        window.localStorage.setItem('custodianRiskChallengeTransaction', transactionId);
        window.location.replace(outboundRedirectUrl);
      } catch (error) {
        datadogLogs.logger.error(
          'Custodian Deposit Transfer - error redirecting to custodian risk challenge',
          { data, error },
        );
        showToast({
          message: 'Unable to redirect for verification.',
          severity: 'error',
        });
      }
    }
  };

  const handleCustodianRiskEngineActionIfAny = async (
    cmid: string | null,
    data: CustodianDepositTransferResponse,
  ) => {
    const { action } = data || {};
    if (action && action.type) {
      const actionType = action.type.trim().toUpperCase();
      switch (actionType) {
        case 'DENY':
          datadogLogs.logger.info(
            'Custodian Deposit Transfer - DENY',
            { actionType, cmid },
          );
          await handleWorkflowCompletion('deny');
          break;
        case 'CHALLENGE':
          datadogLogs.logger.info(
            'Custodian Deposit Transfer - CHALLENGE',
            { actionType, cmid },
          );
          await handleCustodianRiskEngineChallenge(cmid, data);
          break;
        default:
          datadogLogs.logger.info(
            'Custodian Deposit Transfer - UNKNOWN ACTION',
            { actionType, cmid },
          );
          // For unknown actions, allow the standard workflow
          // completion (i.e success) to take place
          custodianActionTakenRef.current = false;
          break;
      }
    }
  };

  const CMID = window.sessionStorage.getItem('CMID');
  const buildHeaderContext = ({ sessionId }: HeaderContextArgs = {}) => (sessionId
    && typeof sessionId === 'string'
    && sessionId.trim().length === 32
    ? { headers: { 'session-id': sessionId } } : {});

  const [createDepositTransfer] = useMutation<{ createDepositTransfer: { transfer: { id: string } } }>(CREATE_DEPOSIT_TRANSFER);
  const [createScheduledDepositTransfer] = useMutation<{ createScheduledDepositTransfer: { scheduledTransfer: { id: string } } }>(
    CREATE_SCHEDULED_DEPOSIT_TRANSFER,
    {
      onCompleted: () => {
        updateContextTransfer({
          value: { ...applicableData, skipDocuments: false },
          existingContext: applicableData,
          setApplicationContext,
          setWorkflowCompletionContext,
        });
      },
    },
  );
  const [createTransferBetweenSubAccounts] = useMutation<{ transferBetweenSubAccounts: { transfer: { id: string } } }>(TRANSFER_BETWEEN_SUB_ACCOUNTS);
  const [createTransferAllBetweenSubAccounts] = useMutation<{ transferAllBetweenSubAccounts: { transfer: { id: string } } }>(TRANSFER_ALL_BETWEEN_SUB_ACCOUNTS);
  const [createExternalTransfer] = useMutation<{ createExternalTransfer: { manualProcess: ManualProcess } }>(CREATE_EXTERNAL_TRANSFER);
  const [createCustodianTransfer] = useMutation<{
    createCustodianDepositTransfer: CustodianDepositTransferResponse
  }>(
    CREATE_CUSTODIAN_TRANSFER,
    {
      onError: (error) => {
        hasErrorRef.current = true;
        handleWorkflowCompletion('failure');
      },
      onCompleted(data) {
        const hasAction = data.createCustodianDepositTransfer.action != null
          && typeof data.createCustodianDepositTransfer.action === 'object';
        if (hasAction) {
          custodianActionTakenRef.current = true;
          handleCustodianRiskEngineActionIfAny(CMID, data.createCustodianDepositTransfer);
        }
      },
    },
  );

  const fullTransfer = () => applicableData?.fromSubAccount?.statistics?.marketValueCents && applicableData?.amountCents > (applicableData?.fromSubAccount?.statistics?.marketValueCents || 0) * 0.97;

  const submit = async () => {
    setLoading(true);
    custodianActionTakenRef.current = false;
    hasErrorRef.current = false;

    try {
      if (applicableData?.type === 'EFT') {
        if (applicableData?.schedule === 'ONE_TIME' && !applicableData?.scheduledDate) {
          if (options?.createExternalDepositTransfer) {
            const input = {
              accountId: applicableData?.subAccount?.account?.id,
              amount: applicableData?.amountCents,
              fund: applicableData?.holdings?.financialProduct?.cusip,
              frequency: applicableData?.schedule,
              startDate: new Date().toISOString().split('T')[0],
              bankAccount: {
                accountNumber: applicableData?.bankAccount?.bankAccountNumberDisclosed,
                institutionNumber: applicableData?.bankAccount?.institutionNumber,
                transitNumber: applicableData?.bankAccount?.transitNumberDisclosed,
              },
            };
            const { data } = await createCustodianTransfer({
              variables: { input },
              ...(CMID && {
                context: {
                  ...buildHeaderContext({ sessionId: CMID }),
                },
              }),
            });
            if (data?.createCustodianDepositTransfer) {
              const { createCustodianDepositTransfer } = data;
              await recordWorkflowCustodianDepositTransfer({
                ...input,
                confirmationNumber: createCustodianDepositTransfer.confirmationNumber,
              });
            }
          } else {
            const { data } = await createDepositTransfer({
              variables: {
                input: {
                  amountCents: applicableData?.amountCents,
                  bankAccountId: applicableData?.bankAccount.id,
                  subAccountId: applicableData?.subAccount.id,
                  fiscalYear: applicableData?.fiscalYear,
                  ...((enableMultiCurrencyDepositTransfers) ? { currency: applicableData?.bankAccount?.currency } : {}),
                },
              },
            });
            if (data?.createDepositTransfer?.transfer) {
              const { createDepositTransfer: { transfer } } = data;
              await recordWorkflowTransfers(transfer);
            }
          }
        } else if (options?.createExternalDepositTransfer) {
          const input = {
            accountId: applicableData?.subAccount?.account?.id,
            amount: applicableData?.amountCents,
            fund: applicableData?.holdings?.financialProduct?.cusip,
            frequency: applicableData?.schedule,
            startDate: applicableData?.scheduledDate,
            bankAccount: {
              accountNumber: applicableData?.bankAccount?.bankAccountNumberDisclosed,
              institutionNumber: applicableData?.bankAccount?.institutionNumber,
              transitNumber: applicableData?.bankAccount?.transitNumberDisclosed,
            },
          };
          const { data } = await createCustodianTransfer({
            variables: { input },
            ...(CMID && {
              context: {
                ...buildHeaderContext({ sessionId: CMID }),
              },
            }),
          });
          if (data?.createCustodianDepositTransfer) {
            const { createCustodianDepositTransfer } = data;
            await recordWorkflowCustodianDepositTransfer({
              ...input,
              confirmationNumber: createCustodianDepositTransfer.confirmationNumber,
            });
          }
        } else {
          const { data } = await createScheduledDepositTransfer({
            variables: {
              input: {
                amountCents: applicableData?.amountCents,
                bankAccountId: applicableData?.bankAccount.id,
                subAccountId: applicableData?.subAccount.id,
                scheduledDate: applicableData?.scheduledDate,
                frequency: applicableData?.schedule,
                fiscalYear: applicableData?.fiscalYear ?? undefined,
                ...((enableMultiCurrencyDepositTransfers) ? { currency: applicableData?.bankAccount?.currency } : {}),
              },
            },
          });
          if (data?.createScheduledDepositTransfer?.scheduledTransfer) {
            const { createScheduledDepositTransfer: { scheduledTransfer } } = data;
            await recordWorkflowScheduledTransfers(scheduledTransfer);
          }
        }
      } else if (applicableData?.type === 'INTERNAL_TRANSFER') {
        if (!fullTransfer()) {
          const { data } = await createTransferBetweenSubAccounts({
            variables: {
              input: {
                amountCents: applicableData?.amountCents,
                fromSubAccountId: applicableData?.fromSubAccount.id,
                toSubAccountId: applicableData?.subAccount.id,
                fiscalYear: applicableData?.fiscalYear ?? undefined,
                ...((enableMultiCurrencyDepositTransfers && applicableData?.fromSubAccount?.account?.baseCurrency) ? { currency: applicableData?.fromSubAccount?.account?.baseCurrency } : {}),
              },
            },
          });
          if (data?.transferBetweenSubAccounts?.transfer) {
            const { transferBetweenSubAccounts: { transfer } } = data;
            await recordWorkflowTransfers(transfer);
          }
        } else {
          const { data } = await createTransferAllBetweenSubAccounts({
            variables: {
              input: {
                fromSubAccountId: applicableData?.fromSubAccount.id,
                toSubAccountId: applicableData?.subAccount.id,
                fiscalYear: applicableData?.fiscalYear ?? undefined,
                ...((enableMultiCurrencyDepositTransfers && applicableData?.fromSubAccount?.account?.baseCurrency) ? { currency: applicableData?.fromSubAccount?.account?.baseCurrency } : {}),
              },
            },
          });
          if (data?.transferAllBetweenSubAccounts?.transfer) {
            const { transferAllBetweenSubAccounts: { transfer } } = data;
            await recordWorkflowTransfers(transfer);
          }
        }
      } else {
        const { data } = await createExternalTransfer({
          variables: {
            input: {
              amountCents: applicableData?.amountCents,
              subAccountId: applicableData?.subAccount.id,
              institution: { id: applicableData?.institution.id },
              transferAccountNumber: applicableData?.accountNumber,
              transferMethod: applicableData?.transferType,
              transferAccount: applicableData?.accountType,
              participantNumber: participantNumberEnabled && applicableData?.participantNumber ? applicableData?.participantNumber : undefined,
            },
          },
        });
        if (data?.createExternalTransfer) {
          const { createExternalTransfer: { manualProcess } } = data;
          await recordWorkflowExternalTransfer(manualProcess);
        }
      }

      // Only complete workflow if no custodian action was taken and no error occurred.
      // Custodian actions handle completion in their onCompleted callbacks.
      // Using refs to prevent race conditions with React's async state updates
      if (!custodianActionTakenRef.current && !workflowCompletedRef.current && !hasErrorRef.current) {
        await handleWorkflowCompletion('success');
      } else {
        await handleWorkflowCompletion('silent');
      }
    } catch (error) {
      setLoading(false);
      showToast({
        message: t('depositReview.error'),
        severity: 'error',
      });
    }
  };

  return (
    <WorkflowCompletionForm onSubmit={submit} submitButtonProps={{ label: t('depositReview.createDeposit'), disabled: loading, dataTestId: 'deposit-review-continue-button' }} >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>{translateBackend(options?.title)}</Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>{translateBackend(options?.subtitle)}</Typography>
      {applicableData?.type === 'EFT' && (
        <Stack spacing={3}>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.fromBankAccount')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{`${applicableData?.bankAccount?.name} ${applicableData?.bankAccount?.bankAccountNumber}`}</Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.toSubAccount')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{getSubAccountName(applicableData?.subAccount)}</Typography>
          </Box>
          {options?.showHoldings && (
            <Box>
              <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.toHolding')}</Typography>
              <Typography variant='bodyLarge' weight='bold'>
                {translateBackend(applicableData?.holdings?.financialProduct?.translatedNickName ?? applicableData?.holdings?.financialProduct?.translatedName)}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.amount')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{formatMoneyValue(applicableData?.amountCents)}</Typography>
          </Box>
          {applicableData?.schedule !== 'ONE_TIME' && (
            <>
              <Box>
                <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.schedule')}</Typography>
                <Typography variant='bodyLarge' weight='bold'>{t(`schedule.${applicableData?.schedule}`)}</Typography>
              </Box>
              <Box>
                <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.startsOn')}</Typography>
                <Typography variant='bodyLarge' weight='bold'>{localizedDate(applicableData?.scheduledDate)}</Typography>
              </Box>
            </>
          )}
          <DisclaimerBox text={translateBackend(options?.eftDisclaimer)} />
        </Stack>
      )}
      {applicableData?.type === 'INTERNAL_TRANSFER' && (
        <Stack spacing={3}>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.fromSubAccount')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{getSubAccountName(applicableData?.fromSubAccount)}</Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.toSubAccount')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{getSubAccountName(applicableData?.subAccount)}</Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.amount')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{fullTransfer() ? t('depositReview.fullTransfer') : formatMoneyValue(applicableData?.amountCents)}</Typography>
          </Box>
          <DisclaimerBox text={translateBackend(options?.internalTransferDisclaimer)} />
        </Stack>
      )}
      {applicableData?.type === 'EXTERNAL_TRANSFER' && (
        <Stack spacing={3}>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.toSubAccount')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{getSubAccountName(applicableData?.subAccount)}</Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.financialInstitution')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{applicableData?.institution.name}</Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.accountNumber')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{applicableData?.accountNumber}</Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.accountType')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{applicableData?.accountType}</Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.transferType')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{t(`depositReview.${applicableData?.transferType}`)}</Typography>
          </Box>
          <Box>
            <Typography variant='labelLarge' colorVariant='variant'>{t('depositReview.amount')}</Typography>
            <Typography variant='bodyLarge' weight='bold'>{fullTransfer() ? t('depositReview.fullTransfer') : formatMoneyValue(applicableData?.amountCents)}</Typography>
          </Box>
          <DisclaimerBox text={translateBackend(options?.externalTransferDisclaimer)} />
        </Stack>
      )}
    </WorkflowCompletionForm>
  );
};

const DisclaimerBox = ({ text }: { text: string }) => {
  const { sys } = useThemeTokens();

  return (
    <Box p={1} sx={{ background: sys.color.surfaceContainerVariant, borderRadius: sys.borderRadius.md }}>
      <Typography variant='bodySmall' mx={1} my={-1}>
        <ReactMarkdown linkTarget="_blank" remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </Typography>
    </Box>
  );
};

export default DepositReview;
